import { FC, useEffect, useState } from "react";
import DashboardContentLayout from "../components/dashboard-content-layout";
import { useNavigate, useParams } from "react-router-dom";
import { AdRoutes } from "../../../constants/ad-routes";

import {
  Box,
  Button,
  Checkbox,
  Divider,
  TextField,
  Typography,
} from "@mui/material";

import { ControlledSelect, ControlledSelectOption, makeSxStyles } from "ui";
import { useFormik } from "formik";
import { toast } from "react-toastify";

import { Delete } from "@mui/icons-material";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { useDialogStore } from "../../../state/dialog-store";
import {
  adminPostVehicleReqDtoSchema,
  useVehicleControllerCreate,
  useVehicleControllerDelete,
  useVehicleControllerGetVehicleById,
  useVehicleControllerUpdate,
  useVehicleTypeControllerFindMany,
} from "service";
import { AdminVehicleTypeResDto } from "service/api/estabrookSchemas";
import { queryKeyFn } from "service/api/estabrookContext";
import { queryClient } from "../../../query-client";
import Navbar from "../../../components/navbar/navbar";
import HeaderItems, {
  HeaderItemsLeft,
  HeaderItemsRight,
} from "../../../shared/components/header-items/HeaderItems";
import useUnsavedChangesBlocker from "../../../shared/hooks/useUnsavedChangesBlocker";

export type AdminPostVehicleDto = {
  licensePlate: string;
  vehicleTypeId: number | null;
  status: boolean;
  travelSpeed: number;
};

const AddVehiclePage: FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { setDialog } = useDialogStore();
  const [select, setSelect] = useState<ControlledSelectOption[]>([]);
  const { vehicleId } = useParams<{
    vehicleId: string;
  }>();

  const { mutate, isLoading } = useVehicleControllerCreate({
    onSuccess: () => {
      toast.success("Vehicle Created Successfully");
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/vehicles",
          operationId: "vehicleControllerGetAll",
          variables: {
            queryParams: {
              limit: 10,
              page: 1,
            },
          },
        }),
      );
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/vehicles/unassigned",
          operationId: "vehicleControllerGetUnassignedVehicles",
          variables: {},
        }),
      );
      resetForm();
      setTimeout(() => {
        navigate(AdRoutes.fleetManagement);
      }, 10);
    },
  });

  const { data: vehicleTypeData } = useVehicleTypeControllerFindMany({
    queryParams: {
      limit: 999,
    },
  });

  const { data: vehicleData, isLoading: loadingVehicle } =
    useVehicleControllerGetVehicleById({
      pathParams: {
        vehicleId: vehicleId || "",
      },
    });

  const { mutate: updateVehicle } = useVehicleControllerUpdate({
    onSuccess: async () => {
      toast.success("Vehicle Updated Successfully");
      await Promise.all([
        queryClient.invalidateQueries(
          queryKeyFn({
            path: "/api/v1/admin/vehicles",
            operationId: "vehicleControllerGetAll",
            variables: {
              queryParams: {
                limit: 10,
                page: 1,
              },
            },
          }),
        ),
        queryClient.invalidateQueries(
          queryKeyFn({
            path: "/api/v1/admin/vehicles/unassigned",
            operationId: "vehicleControllerGetUnassignedVehicles",
            variables: {},
          }),
        ),
        queryClient.invalidateQueries(
          queryKeyFn({
            path: "/api/v1/admin/vehicles/{vehicleId}",
            operationId: "vehicleControllerGetVehicleById",
            variables: {
              pathParams: {
                vehicleId: vehicleId || "",
              },
            },
          }),
        ),
      ]);

      resetForm();
      setTimeout(() => {
        navigate(AdRoutes.fleetManagement);
      }, 10);
    },
  });

  const { mutate: removeVehicle } = useVehicleControllerDelete({
    onSuccess: () => {
      toast.success("Vehicle Deleted Successfully");
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/vehicles",
          operationId: "vehicleControllerGetAll",
          variables: {},
        }),
      );
      navigate(AdRoutes.fleetManagement);
    },
  });

  const {
    values,
    handleChange,
    handleBlur,
    dirty,
    isValid,
    touched,
    errors,
    submitForm,
    resetForm,
    setFieldTouched,
    setFieldValue,
  } = useFormik<AdminPostVehicleDto>({
    initialValues: {
      licensePlate: "",
      vehicleTypeId: null,
      status: false,
      travelSpeed: 0,
    },
    validationSchema: toFormikValidationSchema(adminPostVehicleReqDtoSchema),
    onSubmit: (values) => {
      if (!vehicleId) {
        return mutate({
          body: {
            available: values.status,
            licensePlate: values.licensePlate,
            vehicleTypeId: values.vehicleTypeId || 0,
          },
        });
      }
      return updateVehicle({
        body: {
          available: values.status,
          licensePlate: values.licensePlate,
          vehicleTypeId: values.vehicleTypeId || 0,
        },
        pathParams: {
          vehicleId: vehicleId,
        },
      });
    },
  });

  useUnsavedChangesBlocker(dirty);

  useEffect(() => {
    if (vehicleTypeData && vehicleTypeData.data) {
      setSelect(
        vehicleTypeData.data.reduce(
          (
            acc: ControlledSelectOption[],
            vehicleType: AdminVehicleTypeResDto,
          ) => {
            acc.push({
              label: vehicleType?.typeName || "",
              value: JSON.stringify(vehicleType?.id),
            });
            return acc;
          },
          [] as ControlledSelectOption[],
        ),
      );
    }
  }, [vehicleTypeData]);

  useEffect(() => {
    if (vehicleId && !loadingVehicle && vehicleData?.data) {
      resetForm({
        values: {
          licensePlate: vehicleData?.data?.licensePlate,
          vehicleTypeId: vehicleData?.data?.vehicleType?.id || null,
          status: vehicleData?.data?.available,
          travelSpeed: vehicleData.data.travelSpeed,
        },
      });
    }
  }, [vehicleData, loadingVehicle]);

  const onDeleteVehicle = () => {
    setDialog({
      title: "Delete Vehicle",
      description: `Are you sure you want to delete the vehicle ${values?.licensePlate} - ${values?.vehicleTypeId}? This will remove it from all assigned routes and cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      withCancelAction: true,
      onConfirm: async (onClose) => {
        removeVehicle({ pathParams: { vehicleId: vehicleId || "" } });
        onClose();
      },
    });
  };

  return (
    <DashboardContentLayout>
      <Navbar showAccountMenu>
        <HeaderItems>
          <HeaderItemsLeft
            breadcrumbs={[
              {
                label: "Fleet Management",
                url: AdRoutes.fleetManagement,
              },
              {
                label: vehicleId ? "Edit Vehicle" : "Add New Vehicle",
              },
            ]}
          />
          <HeaderItemsRight>
            <Button
              disabled={isLoading || !dirty || !isValid}
              variant="contained"
              onClick={submitForm}
              data-testid="save-vehicle"
            >
              {vehicleId ? "Save Changes" : "Add Vehicle"}
            </Button>
          </HeaderItemsRight>
        </HeaderItems>
      </Navbar>
      <Box sx={styles.gridDetailContainer}>
        <Typography fontWeight={600}>License Plate</Typography>
        <TextField
          id="licensePlate"
          type="text"
          value={values.licensePlate}
          onChange={handleChange}
          onBlur={handleBlur}
          helperText={touched.licensePlate && errors.licensePlate}
          error={touched.licensePlate && !!errors.licensePlate}
          margin="dense"
          variant="outlined"
          fullWidth
        />
        <Typography fontWeight={600}>Vehicle Type</Typography>
        <ControlledSelect
          data-testid="vehicle-type"
          placeholderSelectEnabled={false}
          placeholder="Vehicle Type"
          options={select}
          value={values.vehicleTypeId?.toString()}
          onChange={(value) => {
            setFieldValue("vehicleTypeId", parseInt(value));
            setFieldTouched("vehicleTypeId", true);
          }}
          onBlur={handleBlur}
        />
        <Typography fontWeight={600}>Availability</Typography>
        <Checkbox
          id="status"
          checked={values.status}
          sx={{ justifySelf: "flex-start" }}
          onChange={handleChange}
        />
      </Box>
      {vehicleId && (
        <>
          <Divider sx={styles.divider} />
          <Box sx={styles.gridDetailContainer}>
            <Typography fontWeight={600} alignSelf="start">
              Delete Vehicle
            </Typography>
            <Box display="grid" gap="20px" alignItems="flex-start">
              <Typography variant="caption">
                Before deleting this vehicle, please note that doing so will
                result in regions assigned to this vehicle in being unassigned.
              </Typography>
              <Button
                color="error"
                variant="outlined"
                sx={styles.deleteButton}
                onClick={onDeleteVehicle}
                startIcon={<Delete />}
                data-testid="delete-vehicle"
              >
                Delete Vehicle
              </Button>
            </Box>
          </Box>
        </>
      )}
    </DashboardContentLayout>
  );
};

const useStyles = makeSxStyles(() => ({
  gridDetailContainer: {
    mt: "40px",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    rowGap: "16px",
    maxWidth: "50%",
    alignItems: "center",
  },
  divider: {
    mt: "40px",
  },
}));

export default AddVehiclePage;
